import React, { useState, useRef, useCallback } from 'react';
import { 
  Phone, Mail, Clock, Package, MessageSquare, Eye, Send, 
  CheckCircle2, XCircle, PlayCircle, Filter, Trash2, User, FileText, RefreshCw, Wifi, WifiOff,
  Download, Image as ImageIcon, ExternalLink, LayoutGrid, List, GripVertical, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { updateQuote, deleteQuote } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/api')) return `${API_URL}${url}`;
  return `${API_URL}${url}`;
};

const QuotesSection = ({ quotes, contacts, onRefresh, onManualRefresh, wsConnected }) => {
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailModal, setDetailModal] = useState({ open: false, quote: null });
  const [internalNotes, setInternalNotes] = useState('');
  const [downloadingImage, setDownloadingImage] = useState(null);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [pointerDrag, setPointerDrag] = useState(null);
  const [statusOverrides, setStatusOverrides] = useState({});
  const dragRef = useRef(null);
  const canvasRef = useRef(null);

  const quoteStatuses = [
    { value: 'pendente', label: 'Pendente', color: '#f59e0b', bgColor: '#fef3c7', icon: Clock },
    { value: 'enviado', label: 'Enviado', color: '#3b82f6', bgColor: '#dbeafe', icon: Send },
    { value: 'em_andamento', label: 'Em Andamento', color: '#8b5cf6', bgColor: '#ede9fe', icon: PlayCircle },
    { value: 'aprovado', label: 'Aprovado', color: '#22c55e', bgColor: '#dcfce7', icon: CheckCircle2 },
    { value: 'recusado', label: 'Recusado', color: '#ef4444', bgColor: '#fee2e2', icon: XCircle },
  ];

  const getStatusInfo = (status) => {
    return quoteStatuses.find(s => s.value === status) || quoteStatuses[0];
  };

  const downloadHighResImage = useCallback(async (item, itemIndex) => {
    if (!item.image_url) return;
    setDownloadingImage(itemIndex);
    try {
      const outputSize = 1600;
      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = getFileUrl(item.image_url);
      });
      ctx.beginPath();
      ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, outputSize, outputSize);
      const adjustment = item.image_adjustment || { scale: 1, x: 0, y: 0 };
      const scale = adjustment.scale || 1;
      const offsetX = (adjustment.x || 0) / 100;
      const offsetY = (adjustment.y || 0) / 100;
      const scaledSize = outputSize * scale;
      const drawX = (outputSize - scaledSize) / 2 + (offsetX * outputSize);
      const drawY = (outputSize - scaledSize) / 2 + (offsetY * outputSize);
      ctx.drawImage(img, drawX, drawY, scaledSize, scaledSize);
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `cevador-${item.product_name?.replace(/\s+/g, '-') || 'arte'}-1600px.png`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar imagem:', error);
      alert('Erro ao gerar imagem. Tente novamente.');
    } finally {
      setDownloadingImage(null);
    }
  }, []);

  const getQuoteStatus = (quote) => statusOverrides[quote.id] || quote.status || 'pendente';

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuote(quoteId, { status: newStatus });
      onRefresh();
    } catch (error) {
      console.error('Error updating quote status:', error);
    }
  };

  const moveQuote = async (quoteId, newStatus) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote || getQuoteStatus(quote) === newStatus) return;
    setStatusOverrides(prev => ({ ...prev, [quoteId]: newStatus }));
    try {
      await updateQuote(quoteId, { status: newStatus });
      onRefresh();
    } catch (error) {
      setStatusOverrides(prev => {
        const { [quoteId]: removed, ...rest } = prev;
        return rest;
      });
      onRefresh();
      alert('Erro ao mover orçamento');
    }
  };

  const startPointerDrag = (e, quote) => {
    if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;
    const startX = e.clientX;
    const startY = e.clientY;
    dragRef.current = { quote, startX, startY, started: false };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      if (!d.started) {
        if (Math.abs(ev.clientX - d.startX) < 6 && Math.abs(ev.clientY - d.startY) < 6) return;
        d.started = true;
        document.body.style.userSelect = 'none';
      }
      setPointerDrag({ quoteId: d.quote.id, quote: d.quote, x: ev.clientX, y: ev.clientY });
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const col = el && el.closest('[data-column-status]');
      setDragOverColumn(col ? col.getAttribute('data-column-status') : null);
    };

    const onUp = (ev) => {
      const d = dragRef.current;
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
      setPointerDrag(null);
      setDragOverColumn(null);
      if (d && d.started) {
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const col = el && el.closest('[data-column-status]');
        if (col) moveQuote(d.quote.id, col.getAttribute('data-column-status'));
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const downloadCompactPdf = (quoteId) => {
    window.open(`${API_URL}/api/quotes/${quoteId}/pdf-compact`, '_blank');
  };

  const handleSaveNotes = async (quoteId) => {
    try {
      await updateQuote(quoteId, { internal_notes: internalNotes });
      setDetailModal({ open: false, quote: null });
      onRefresh();
    } catch (error) {
      alert('Erro ao salvar anotações');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Excluir este orçamento?')) return;
    try {
      await deleteQuote(quoteId);
      onRefresh();
    } catch (error) {
      alert('Erro ao excluir orçamento');
    }
  };

  const toggleExpand = (quoteId) => {
    setExpandedQuotes(prev => ({ ...prev, [quoteId]: !prev[quoteId] }));
  };

  const openDetails = (quote) => {
    setDetailModal({ open: true, quote });
    setInternalNotes(quote.internal_notes || '');
  };

  const filteredQuotes = statusFilter === 'all' 
    ? quotes 
    : quotes.filter(q => getQuoteStatus(q) === statusFilter);

  const statusCounts = quoteStatuses.reduce((acc, status) => {
    acc[status.value] = quotes.filter(q => getQuoteStatus(q) === status.value).length;
    return acc;
  }, {});

  // Compact Quote Card for Kanban
  const KanbanCard = ({ quote }) => {
    const statusInfo = getStatusInfo(quote.status || 'pendente');
    const total = quote.items?.reduce((sum, item) => sum + ((item.unit_price || 0) * item.quantity), 0) || 0;
    const itemCount = quote.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
    
    return (
      <div 
        onPointerDown={(e) => startPointerDrag(e, quote)}
        style={{ touchAction: 'pan-y' }}
        data-testid={`quote-card-${quote.id}`}
        className={`bg-white rounded-lg shadow-sm border border-slate-200 p-3 mb-2 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing group ${
          pointerDrag?.quoteId === quote.id ? 'opacity-40' : ''
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-800 text-sm truncate">{quote.customer_name}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {quote.customer_phone}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(quote.id); }}
            className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
        
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-slate-500">{itemCount} itens</span>
          <span className="font-bold text-[#195C75]">R$ {total.toFixed(2).replace('.', ',')}</span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-400">
            {new Date(quote.created_at).toLocaleDateString('pt-BR')}
          </span>
          <div className="flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); downloadCompactPdf(quote.id); }}
              className="p-1.5 bg-[#195C75] text-white rounded hover:bg-[#134a5e] transition-colors"
              title="Baixar lista de itens (PDF)"
              data-testid={`quote-compact-pdf-${quote.id}`}
            >
              <FileText className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/55${quote.customer_phone?.replace(/\D/g, '')}`, '_blank'); }}
              className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              title="WhatsApp"
            >
              <Phone className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); openDetails(quote); }}
              className="p-1.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors"
              title="Detalhes"
            >
              <Eye className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Compact List Row
  const ListRow = ({ quote }) => {
    const statusInfo = getStatusInfo(quote.status || 'pendente');
    const StatusIcon = statusInfo.icon;
    const total = quote.items?.reduce((sum, item) => sum + ((item.unit_price || 0) * item.quantity), 0) || 0;
    const itemCount = quote.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
    const isExpanded = expandedQuotes[quote.id];
    
    return (
      <div className="border-b border-slate-100 last:border-b-0">
        {/* Main Row */}
        <div className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors">
          {/* Status Indicator */}
          <div 
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: statusInfo.color }}
            title={statusInfo.label}
          />
          
          {/* Customer Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800 text-sm truncate">{quote.customer_name}</span>
              <span className="text-xs text-slate-400 hidden sm:inline">•</span>
              <span className="text-xs text-slate-500 hidden sm:inline">{quote.customer_phone}</span>
            </div>
          </div>
          
          {/* Items Count */}
          <div className="hidden md:flex items-center gap-1 text-xs text-slate-500 w-20">
            <Package className="w-3 h-3" />
            {itemCount} itens
          </div>
          
          {/* Total */}
          <div className="w-24 text-right">
            <span className="font-bold text-sm text-[#195C75]">R$ {total.toFixed(2).replace('.', ',')}</span>
          </div>
          
          {/* Status Select */}
          <select
            value={quote.status || 'pendente'}
            onChange={(e) => handleStatusChange(quote.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="text-xs border rounded px-2 py-1 font-medium w-28"
            style={{ 
              color: statusInfo.color,
              borderColor: statusInfo.color,
              backgroundColor: statusInfo.bgColor
            }}
          >
            {quoteStatuses.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          
          {/* Date */}
          <span className="text-xs text-slate-400 w-20 text-right hidden lg:block">
            {new Date(quote.created_at).toLocaleDateString('pt-BR')}
          </span>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => downloadCompactPdf(quote.id)}
              className="p-1.5 bg-[#195C75] text-white rounded hover:bg-[#134a5e] transition-colors"
              title="Baixar lista de itens (PDF)"
            >
              <FileText className="w-3 h-3" />
            </button>
            <button
              onClick={() => window.open(`https://wa.me/55${quote.customer_phone?.replace(/\D/g, '')}`, '_blank')}
              className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              title="WhatsApp"
            >
              <Phone className="w-3 h-3" />
            </button>
            <button
              onClick={() => openDetails(quote)}
              className="p-1.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors"
              title="Detalhes"
            >
              <Eye className="w-3 h-3" />
            </button>
            <button
              onClick={() => toggleExpand(quote.id)}
              className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button
              onClick={() => handleDelete(quote.id)}
              className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 pb-3 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {quote.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white rounded px-2 py-1.5">
                  {item.image_url && (
                    <img src={getFileUrl(item.image_url)} alt="" className="w-8 h-8 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-slate-700">{item.product_name}</p>
                    <p className="text-slate-500">{item.quantity}x R$ {(item.unit_price || 0).toFixed(2)}</p>
                  </div>
                  {item.file_url && (
                    <a href={getFileUrl(item.file_url)} target="_blank" rel="noopener noreferrer" className="text-[#195C75]">
                      <Download className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Orçamentos</h2>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded ${viewMode === 'kanban' ? 'bg-white shadow-sm text-[#195C75]' : 'text-slate-500'}`}
              title="Kanban"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-[#195C75]' : 'text-slate-500'}`}
              title="Lista"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={onManualRefresh}
            className="p-2 text-slate-400 hover:text-[#195C75] hover:bg-slate-100 rounded-lg transition-colors"
            title="Atualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <div 
            className={`p-2 rounded-lg ${wsConnected ? 'text-emerald-500' : 'text-slate-300'}`}
            title={wsConnected ? 'Tempo real ativo' : 'Tempo real desconectado'}
          >
            {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {quoteStatuses.map(status => {
            const StatusIcon = status.icon;
            const statusQuotes = quotes.filter(q => getQuoteStatus(q) === status.value);
            
            return (
              <div 
                key={status.value}
                data-column-status={status.value}
                data-testid={`kanban-column-${status.value}`}
                className={`flex-shrink-0 w-72 bg-slate-50 rounded-xl transition-all ${
                  dragOverColumn === status.value && pointerDrag ? 'ring-2 ring-[#195C75] ring-offset-2 bg-slate-100' : ''
                }`}
              >
                {/* Column Header */}
                <div 
                  className="p-3 rounded-t-xl flex items-center justify-between"
                  style={{ backgroundColor: status.bgColor }}
                >
                  <div className="flex items-center gap-2">
                    <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
                    <span className="font-medium text-sm" style={{ color: status.color }}>
                      {status.label}
                    </span>
                  </div>
                  <span 
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: status.color, color: 'white' }}
                  >
                    {statusCounts[status.value] || 0}
                  </span>
                </div>
                
                {/* Cards Container */}
                <div className="p-2 max-h-[60vh] overflow-y-auto">
                  {statusQuotes.length === 0 ? (
                    <p className="text-center text-xs text-slate-400 py-4">Nenhum orçamento</p>
                  ) : (
                    statusQuotes.map(quote => (
                      <KanbanCard key={quote.id} quote={quote} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* List Header */}
          <div className="flex items-center gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500">
            <div className="w-2" />
            <div className="flex-1">Cliente</div>
            <div className="hidden md:block w-20">Itens</div>
            <div className="w-24 text-right">Total</div>
            <div className="w-28">Status</div>
            <div className="hidden lg:block w-20 text-right">Data</div>
            <div className="w-28">Ações</div>
          </div>
          
          {/* Status Filter Pills */}
          <div className="flex gap-2 p-3 border-b border-slate-100 overflow-x-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                statusFilter === 'all' 
                  ? 'bg-[#195C75] text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Todos ({quotes.length})
            </button>
            {quoteStatuses.map(status => (
              <button
                key={status.value}
                onClick={() => setStatusFilter(status.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  statusFilter === status.value 
                    ? 'text-white' 
                    : 'text-slate-600 hover:opacity-80'
                }`}
                style={{ 
                  backgroundColor: statusFilter === status.value ? status.color : status.bgColor,
                  color: statusFilter === status.value ? 'white' : status.color
                }}
              >
                {status.label} ({statusCounts[status.value] || 0})
              </button>
            ))}
          </div>
          
          {/* List Items */}
          {filteredQuotes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum orçamento encontrado</p>
            </div>
          ) : (
            filteredQuotes.map(quote => (
              <ListRow key={quote.id} quote={quote} />
            ))
          )}
        </div>
      )}

      {/* Floating drag ghost */}
      {pointerDrag && (
        <div
          className="fixed z-[9999] pointer-events-none w-64 bg-white rounded-lg shadow-2xl border-2 border-[#195C75] p-3 opacity-95 rotate-2"
          style={{ left: pointerDrag.x + 10, top: pointerDrag.y + 10 }}
          data-testid="drag-ghost"
        >
          <p className="font-medium text-slate-800 text-sm truncate">{pointerDrag.quote.customer_name}</p>
          <p className="text-xs text-slate-500">{pointerDrag.quote.customer_phone}</p>
          <p className="text-xs font-bold text-[#195C75] mt-1">
            R$ {(pointerDrag.quote.items?.reduce((sum, item) => sum + ((item.unit_price || 0) * item.quantity), 0) || 0).toFixed(2).replace('.', ',')}
          </p>
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ open: false, quote: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailModal.quote && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Orçamento #{detailModal.quote.id?.slice(-8).toUpperCase()}</span>
                  <span 
                    className="text-sm px-3 py-1 rounded-full"
                    style={{ 
                      backgroundColor: getStatusInfo(detailModal.quote.status).bgColor,
                      color: getStatusInfo(detailModal.quote.status).color
                    }}
                  >
                    {getStatusInfo(detailModal.quote.status).label}
                  </span>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Cliente
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Nome:</span>
                      <span className="ml-2 text-slate-800">{detailModal.quote.customer_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Telefone:</span>
                      <span className="ml-2 text-slate-800">{detailModal.quote.customer_phone}</span>
                    </div>
                    {detailModal.quote.customer_email && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Email:</span>
                        <span className="ml-2 text-slate-800">{detailModal.quote.customer_email}</span>
                      </div>
                    )}
                    {detailModal.quote.customer_cep && (
                      <div className="col-span-2">
                        <span className="text-slate-500">CEP:</span>
                        <span className="ml-2 text-slate-800">
                          {detailModal.quote.customer_cep?.replace(/(\d{5})(\d{3})/, '$1-$2')}
                        </span>
                      </div>
                    )}
                    {detailModal.quote.customer_address && (
                      <div className="col-span-2 mt-2 p-3 bg-white rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-500 mb-1">Endereço:</p>
                        <p className="text-slate-800 font-medium">
                          {detailModal.quote.customer_address.street}
                          {detailModal.quote.customer_address.number && `, ${detailModal.quote.customer_address.number}`}
                        </p>
                        {detailModal.quote.customer_address.complement && (
                          <p className="text-slate-600 text-sm">{detailModal.quote.customer_address.complement}</p>
                        )}
                        <p className="text-slate-600 text-sm">
                          {detailModal.quote.customer_address.neighborhood}
                        </p>
                        <p className="text-slate-800">
                          {detailModal.quote.customer_address.city}/{detailModal.quote.customer_address.state}
                        </p>
                      </div>
                    )}
                    {/* Legacy support for old customer_data format */}
                    {!detailModal.quote.customer_address && detailModal.quote.customer_data?.street && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Endereço:</span>
                        <span className="ml-2 text-slate-800">
                          {detailModal.quote.customer_data.street}
                          {detailModal.quote.customer_data.number && `, ${detailModal.quote.customer_data.number}`}
                          {detailModal.quote.customer_data.neighborhood && ` - ${detailModal.quote.customer_data.neighborhood}`}
                          {detailModal.quote.customer_data.city && ` - ${detailModal.quote.customer_data.city}`}
                          {detailModal.quote.customer_data.state && `/${detailModal.quote.customer_data.state}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Items */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Itens ({detailModal.quote.items?.length || 0})
                  </h4>
                  <div className="space-y-2">
                    {detailModal.quote.items?.map((item, idx) => (
                      <div key={idx} className="bg-white border border-slate-200 rounded-lg p-3">
                        <div className="flex gap-3">
                          {item.image_url && (
                            <img 
                              src={getFileUrl(item.image_url)} 
                              alt={item.product_name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          )}
                          {!item.image_url && item.art_type === 'link' && (
                            <div className="w-16 h-16 rounded-lg bg-purple-100 flex items-center justify-center">
                              <ExternalLink className="w-6 h-6 text-purple-500" />
                            </div>
                          )}
                          {!item.image_url && item.art_type === 'request' && (
                            <div className="w-16 h-16 rounded-lg bg-amber-100 flex items-center justify-center">
                              <FileText className="w-6 h-6 text-amber-600" />
                            </div>
                          )}
                          
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{item.product_name}</p>
                            <p className="text-sm text-slate-500">
                              {item.quantity}x R$ {(item.unit_price || 0).toFixed(2)} = 
                              <span className="font-medium text-[#195C75]"> R$ {((item.unit_price || 0) * item.quantity).toFixed(2)}</span>
                            </p>
                            
                            {/* Art Request Description */}
                            {item.art_type === 'request' && item.art_description && (
                              <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200 text-xs">
                                <p className="font-medium text-amber-700 mb-1">🎨 Solicitação:</p>
                                <p className="text-slate-700">{item.art_description}</p>
                              </div>
                            )}
                            
                            {/* Download/Link Buttons */}
                            <div className="flex gap-2 mt-2">
                              {item.file_url && (
                                <>
                                  <a
                                    href={getFileUrl(item.file_url)}
                                    download
                                    className="text-xs bg-[#195C75] text-white px-2 py-1 rounded flex items-center gap-1"
                                  >
                                    <Download className="w-3 h-3" /> Baixar
                                  </a>
                                  <a
                                    href={getFileUrl(item.file_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs border border-[#195C75] text-[#195C75] px-2 py-1 rounded flex items-center gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3" /> Abrir
                                  </a>
                                </>
                              )}
                              {item.art_link && (
                                <a
                                  href={item.art_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs bg-purple-500 text-white px-2 py-1 rounded flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" /> Abrir Link
                                </a>
                              )}
                              {item.image_url && !item.file_url && (
                                <button
                                  onClick={() => downloadHighResImage(item, idx)}
                                  disabled={downloadingImage === idx}
                                  className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded flex items-center gap-1"
                                >
                                  {downloadingImage === idx ? (
                                    <><div className="w-3 h-3 border border-slate-500 border-t-transparent rounded-full animate-spin" /> Gerando...</>
                                  ) : (
                                    <><Download className="w-3 h-3" /> Alta Resolução</>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Total */}
                <div className="bg-[#195C75]/10 rounded-lg p-4 flex items-center justify-between">
                  <span className="font-medium text-slate-700">Total do Orçamento</span>
                  <span className="text-2xl font-bold text-[#195C75]">
                    R$ {(detailModal.quote.items?.reduce((sum, item) => sum + ((item.unit_price || 0) * item.quantity), 0) || 0).toFixed(2).replace('.', ',')}
                  </span>
                </div>
                
                {/* Notes */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Anotações Internas</h4>
                  <Textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Adicione notas internas sobre este orçamento..."
                    className="h-20"
                  />
                </div>
                
                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => downloadCompactPdf(detailModal.quote.id)}
                    className="text-[#195C75] border-[#195C75]"
                    data-testid="modal-compact-pdf-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Lista PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`https://wa.me/55${detailModal.quote.customer_phone?.replace(/\D/g, '')}`, '_blank')}
                    className="text-green-600 border-green-600"
                  >
                    <Phone className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                  <Button
                    onClick={() => handleSaveNotes(detailModal.quote.id)}
                    className="bg-[#195C75]"
                  >
                    Salvar Anotações
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesSection;
